import { useState } from 'react';
import { Link } from 'react-router-dom';
import { APP_VERSION } from '../lib/version';

// Dismissible "what's new" announcement shown at the top of the dashboard for
// everyone. Dismissal is remembered per browser and scoped to the version, so
// bumping APP_VERSION for a future release re-shows it automatically.
//
// Auto-expiry: the banner stops showing on its own after EXPIRES (end of the
// v1.1 launch week), so nobody has to remember to take it down. The permanent
// "v. 1.1" tag in the header remains regardless. To re-announce a future
// release, bump APP_VERSION and push EXPIRES out.
const EXPIRES = '2026-07-13'; // inclusive; hidden from 2026-07-14 onward
const DISMISS_KEY = `amada_version_banner_dismissed_v${APP_VERSION}`;

export default function VersionBanner() {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Past the launch-week window → banner retires itself.
  const today = new Date().toISOString().slice(0, 10);
  if (today > EXPIRES) return null;

  if (dismissed) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore storage errors — banner just won't persist dismissal */
    }
    setDismissed(true);
  };

  return (
    <div className="version-banner" role="status">
      <span className="version-banner-icon" aria-hidden="true">
        ✨
      </span>
      <span className="version-banner-text">
        Welcome to <strong>version {APP_VERSION}</strong>.{' '}
        <Link to="/whats-new" className="version-banner-link">
          Click here
        </Link>{' '}
        for a list of features we&rsquo;ve added.
      </span>
      <button
        type="button"
        className="version-banner-close"
        onClick={dismiss}
        aria-label="Dismiss announcement"
        title="Dismiss"
      >
        ×
      </button>
    </div>
  );
}
